import React, { useContext } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink } from "react-router-dom";

import { useClaimHistory } from "../../api/claim";
import { useInvoiceList } from "../../api/invoice";
import { Loader } from "../../common/components/core";
import { useSelector } from "../../redux/hooks";
import {
    currentRolesSelector,
    currentUserSelector,
} from "../../redux/slices/auth/selectors";
import { ApproveStatus, ClaimStatus, BesuClaimState, ClaimHistory } from "../../types/claim";
import { InvoiceStateWithPaymentInfo } from "../../types/invoice";
import { BesuProjectState } from "../../types/project";
import { UserCurrent } from "../../types/userCurrent";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";
import { isUserContractor, isUserPO } from "../../utils/role";
import InvoiceList from "../invoice/components/InvoiceList";
import ProjectContext from "../project/ProjectContext";

import ClaimContext from "./ClaimContext";
import ClaimDetail from "./components/ClaimDetail";
import useApproveClaimHandler from "./hooks/useApproveClaimHandler";
import useDiscardClaimHandler from "./hooks/useDiscardClaimHandler";
import useRejectClaimHandler from "./hooks/useRejectClaimHandler";
import useHasClaimAction from "./hooks/useHasClaimAction";

const ClaimDetailView = (): JSX.Element => {
    document.title = PageTitle.ClaimDetailView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const {
        data: claim,
    } = useContext(ClaimContext);
    const { data: invoices, isValidating: isInvoicesLoading } = useInvoiceList(claim.referenceId);
    const { data: claimHistory, isValidating: isClaimHistoryLoading } = useClaimHistory(claim.referenceId);
    return (
        <Loader isLoading={isInvoicesLoading || isClaimHistoryLoading}>
            {invoices && claimHistory && (
                <PureClaimDetailView
                    project={project}
                    claim={claim}
                    claimRef={claim.referenceId}
                    claimHistory={claimHistory}
                    invoices={invoices}
                />
            )}
        </Loader>
    );
};

type PureClaimDetailViewProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    claimRef: string;
    claimHistory: ClaimHistory;
    invoices: InvoiceStateWithPaymentInfo[];
};

export const PureClaimDetailView = ({
    project,
    claim,
    claimRef,
    claimHistory,
    invoices,
}: PureClaimDetailViewProps): JSX.Element => {
    const { handler: approve } = useApproveClaimHandler(claimRef);
    const { handler: reject } = useRejectClaimHandler(claimRef);
    const { handler: discard } = useDiscardClaimHandler(claimRef);
    const currentRoles = useSelector(currentRolesSelector);
    const currentUser = useSelector(currentUserSelector);

    const { hasClaimAction } = useHasClaimAction(claim);
    // TODO hot fix, need to have further testing and refactoring
    const isCurrentApproverPending = (
        user: UserCurrent | null,
        claimState: BesuClaimState,
    ): boolean => {
        return (
            claimState.claimApprovers.find(
                (ap) => ap.details.email === user?.email,
            )?.status === ApproveStatus.PENDING
        );
    };
    const isDiscarded = claim.status === ClaimStatus.DISCARDED;
    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    Claim {shortenUUID(claimRef)}
                </Breadcrumb.Item>
            </Breadcrumb>

            <div
                className={`d-flex align-items-center mb-3 ${
                    isDiscarded ? "text-placeholder-grey" : ""
                }`}
            >
                <h1 className="flex-grow-1 mb-0">Claim</h1>
                {(claim.status === ClaimStatus.SUBMITTED ||
                    claim.status === ClaimStatus.RESUBMITTED) &&
                    hasClaimAction && (
                        <>
                            <Button variant="light.tertiary" onClick={reject}>
                                Reject
                            </Button>
                            <Button variant="light.primary" onClick={approve} className="ms-3">
                                Approve
                            </Button>
                        </>
                    )}
                {claim.status === ClaimStatus.PARTIAL_CA_APPROVED &&
                    hasClaimAction &&
                    isCurrentApproverPending(currentUser, claim) && (
                        <>
                            <Button variant="light.primary" onClick={reject}>
                                Reject
                            </Button>
                            <Button variant="light.primary" onClick={approve} className="ms-3">
                                Approve
                            </Button>
                        </>
                    )}
                {claim.status === ClaimStatus.ALL_CA_APPROVED &&
                    isUserPO(currentRoles) && (
                        <>
                            <Button variant="light.primary" onClick={reject}>
                                Reject
                            </Button>
                            <Button variant="light.primary" onClick={approve} className="ms-3">
                                Approve
                            </Button>
                        </>
                    )}
                {(claim.status === ClaimStatus.PO_REJECTED ||
                    claim.status === ClaimStatus.CA_REJECTED) &&
                    hasClaimAction && (
                        <>
                            <Button variant="light.primary" onClick={discard}>
                                Discard
                            </Button>
                            <NavLink
                                to={`/project/${project.referenceId}/claim/${claimRef}/edit`}
                            >
                                <Button variant="light.primary" className="ms-3">
                                    Edit Claim
                                </Button>
                            </NavLink>
                        </>
                    )}
                {isUserContractor(currentRoles) &&
                    claim.status === ClaimStatus.APPROVED && (
                        <NavLink
                            to={`/project/${project.referenceId}/claim/${claimRef}/invoice/new`}
                        >
                            <Button variant="light.primary" className="ms-3">
                                New Invoice
                            </Button>
                        </NavLink>
                    )}
            </div>

            <ClaimDetail
                className={isDiscarded ? "text-placeholder-grey" : ""}
                claim={claim}
                claimHistory={claimHistory}
                isDiscarded={isDiscarded}
            />

            {claim.status === ClaimStatus.APPROVED && (
                <>
                    <h2>Invoice Submissions</h2>
                    <InvoiceList
                        project={project}
                        claim={claim}
                        invoices={invoices}
                    />
                </>
            )}
        </>
    );
};

export default ClaimDetailView;
